import React, { ReactElement, ReactNode, useEffect, useMemo, useState } from 'react';
import { Panel, PanelGroup } from 'react-resizable-panels';

// MUI
import { Grid, useMediaQuery, useTheme } from '@mui/material';

// Openlayers
import { Style } from 'ol/style';
import OlFeature from 'ol/Feature';

// Ui
import MapCentricTable from '@/ui/MapCentric/MapCentricTable';
import MapCentricMiniMap from '@/ui/MapCentric/MapCentricMiniMap';
import MapCentricImagesOverlay from '@/ui/MapCentric/MapCentricImagesOverlay';
import StyledPanelResizeHandle from '@/ui/PanelResize/StyledPanelResizeHandle';

// Lib
import DataController from '@/lib/DataController';

// Context
import { IDialogProps } from '@/context/DialogContext/DialogContext';

// Types
import { DCRecord } from '@/@types/lib/dataController';
import { IModel } from '@/@types/models/model';
import { AllowRowActionFn, RowActionFn } from '@/@types/ui/Table';

export type DialogFormGenerateFunction = (record: DCRecord) => string;

export type MapCentricPageParams = {
  model: IModel;
  titleToken: string;
  view: string | undefined;
  allowAdd?: boolean;
  allowView?: boolean;
  allowEdit?: boolean;
  allowPhotos?: boolean;
  allowExport?: boolean;
  allowExportShape?: boolean;
  allowShowDeleted?: boolean;

  dialogComponent?: React.FunctionComponent<IDialogProps>;
  dialogForm?: string | DialogFormGenerateFunction;
  dialogTitle?: string
  initialSelectedId?: number;

  baseRecordPath: string;

  mapId: number;
  mapModel: IModel;
  allowMinimapHideObjects?: boolean;
  selectedLayerStyle?: (f: any) => Style;

  allowShowImages?: boolean;
  imageRelativePath?: string;
  modelPhotos?: IModel;

  // Open page as dialog
  dialogAsPagePath?: string | undefined;

  onCustomTableAction?: RowActionFn;
  allowCustomTableAction?: AllowRowActionFn;

  // checkbox selection, all selected records are drawn on the map, map clicks toggle them
  multiSelect?: boolean;
  renderSelectionActions?: (refresh: () => void) => ReactElement[];
  // record field holding the map feature id, for lists with several records per map object
  mapRecordField?: string;
  // table-wide controls for the header toolbar
  headerControl?: ReactNode;
};

const MapCentricPage = (props: MapCentricPageParams) => {
  const {
    model,
    view,
    titleToken,
    allowAdd,
    allowView,
    allowEdit,
    allowPhotos,
    allowShowImages,
    allowExport,
    allowExportShape,
    dialogComponent,
    dialogForm,
    dialogTitle,
    initialSelectedId,
    mapId,
    mapModel,
    selectedLayerStyle,
    baseRecordPath,
    imageRelativePath,
    modelPhotos,
    dialogAsPagePath,
    allowMinimapHideObjects = true,
    onCustomTableAction,
    allowCustomTableAction,
    allowShowDeleted,
    multiSelect,
    renderSelectionActions,
    mapRecordField = 'id',
    headerControl,
  } = props;

  const [selectedRecordId, setSelectedRecordId] = useState(initialSelectedId || 0);
  const [showImages, setShowImages] = useState(false);
  const [images, setImages] = useState<DCRecord[]>([]);
  const [imagesButtonEnabled, setImagesButtonEnabled] = useState(false);

  const [records, setRecords] = useState<DCRecord[]>([]);
  const [selectedIds, setSelectedIds] = useState<number[]>([]);
  // bumped only by table changes, so toggling graves on the map doesn't make it jump
  const [fitSelectedKey, setFitSelectedKey] = useState(0);

  const theme = useTheme();
  const mdUp = useMediaQuery(theme.breakpoints.up('md'));

  const selectedFeatures = useMemo(() => {
    if (!multiSelect) return undefined;
    const ids = new Set(selectedIds);
    return records.filter((rec) => ids.has(rec.id as number) && rec.geom).map((rec) => rec.geom as unknown as OlFeature);
  }, [multiSelect, records, selectedIds]);

  const handleTableSelectionChange = (ids: number[]) => {
    setSelectedIds(ids);
    setFitSelectedKey((prev) => prev + 1);
  };

  const handleRecordSelect = (id: number, gfiProperties?: DCRecord) => {
    if (multiSelect) {
      if (!id) return;
      // GFI gives the map object id, a click on an already drawn selection gives the record id
      const key = gfiProperties ? Number(id) : records.find((rec) => rec.id === Number(id))?.[mapRecordField];
      const matched = records.filter((rec) => key !== undefined && Number(rec[mapRecordField]) === Number(key)).map((rec) => rec.id as number);
      if (matched.length === 0) return;
      setSelectedIds((prev) =>
        matched.every((x) => prev.includes(x)) ? prev.filter((x) => !matched.includes(x)) : [...prev, ...matched.filter((x) => !prev.includes(x))]
      );
      return;
    }
    if (id === selectedRecordId) {
      setSelectedRecordId(0);
    } else if (id !== 0 && id !== null) {
      setSelectedRecordId(id);
    } else {
      setSelectedRecordId(0);
    }
  };

  const fetchImages = () => {
    if (selectedRecordId !== 0 && modelPhotos && imageRelativePath) {
      const dc = new DataController(modelPhotos);
      const customPath = `${baseRecordPath}/${selectedRecordId}/${imageRelativePath}`;
      dc.GetData(customPath)
        .then((resp) => {
          if (resp.success && resp.data) {
            if (Array.isArray(resp.data)) {
              setImages(resp.data);
              setImagesButtonEnabled(true);
            } else {
              setImages([]);
              setImagesButtonEnabled(false);
            }
          }
        })
        .catch(() => {
          setImages([]);
          setImagesButtonEnabled(false);
        });
    }
  };

  useEffect(() => {
    if (initialSelectedId) {
      setSelectedRecordId(initialSelectedId);
    }
  }, [initialSelectedId]);

  useEffect(() => {
    fetchImages();
  }, [selectedRecordId]);

  const setAllowImages = () => {
    setShowImages(!showImages);
  };

  const miniMap = (
    <MapCentricMiniMap
      onRecordSelect={handleRecordSelect}
      selectedRecordId={selectedRecordId}
      model={mapModel}
      mapId={mapId}
      allowHideObjects={allowMinimapHideObjects}
      selectedLayerStyle={selectedLayerStyle}
      selectedFeatures={selectedFeatures}
      fitSelectedKey={fitSelectedKey}
    />
  );

  const table = (
    <>
      <MapCentricTable
        model={model}
        titleToken={titleToken}
        view={view}
        onRowAction={onCustomTableAction}
        onAllowRowAction={allowCustomTableAction}
        onRecordSelect={handleRecordSelect}
        selectedRecordId={selectedRecordId}
        setAllowImages={setAllowImages}
        imagesEnabled={imagesButtonEnabled}
        allowAdd={allowAdd}
        allowView={allowView}
        allowEdit={allowEdit}
        allowPhotos={allowPhotos}
        allowExport={allowExport}
        allowExportShape={allowExportShape}
        dialogComponent={dialogComponent}
        dialogForm={dialogForm}
        dialogAsPagePath={dialogAsPagePath}
        dialogTitle={dialogTitle}
        allowShowDeleted={allowShowDeleted}
        multiSelect={multiSelect}
        selectedIds={selectedIds}
        onSelectionChange={handleTableSelectionChange}
        onLoad={multiSelect ? setRecords : undefined}
        renderSelectionActions={renderSelectionActions}
        headerControl={headerControl}
      />
      {allowShowImages && showImages ? (
        <MapCentricImagesOverlay closeOverlay={() => setShowImages(false)} images={images} selectedObjektId={selectedRecordId} />
      ) : null}
    </>
  );

  return mdUp ? (
    <PanelGroup direction="horizontal">
      <Panel defaultSize={35}>{miniMap}</Panel>
      <StyledPanelResizeHandle variant="vertical" />
      <Panel defaultSize={65} minSize={20}>
        {table}
      </Panel>
    </PanelGroup>
  ) : (
    <Grid container columns={24} height="calc( 100vh - 48px )">
      <Grid size={24} sx={{ height: '40%' }}>
        {miniMap}
      </Grid>
      <Grid size={24} sx={{ height: '60%' }} position="relative">
        {table}
      </Grid>
    </Grid>
  );
};

export default MapCentricPage;
